import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import SEO from '@/components/base/SEO';
import { articles, categories } from '@/mocks/data';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find(a => a.slug === slug);

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <i className="ri-file-unknow-line text-5xl text-foreground-300 mb-4 block"></i>
            <h2 className="text-xl font-bold text-foreground-800 mb-2">文章未找到</h2>
            <p className="text-sm text-foreground-500 mb-6">您访问的文章不存在或已被移除</p>
            <Link
              to="/"
              className="inline-flex items-center px-6 py-2.5 bg-primary-500 text-background-50 text-sm font-medium rounded-full hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-arrow-left-line mr-2"></i>
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const cat = categories.find(c => c.slug === article.category);
  const relatedArticles = articles
    .filter(a => a.id !== article.id && a.category === article.category)
    .slice(0, 3);
  const otherArticles = articles
    .filter(a => a.id !== article.id && a.category !== article.category)
    .slice(0, 2);
  const allRelated = [...relatedArticles, ...otherArticles].slice(0, 3);

  const isAccent = cat?.color === 'accent';

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.image,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    dateModified: article.date,
    publisher: {
      '@type': 'Organization',
      name: '精品一区二区',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://jingpinyiquercu.com/article/${article.slug}`,
    },
    keywords: article.tags.join(','),
  };

  return (
    <>
      <SEO
        title={`${article.title} - 精品一区二区`}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://jingpinyiquercu.com/article/${article.slug}`}
        ogType="article"
        ogImage={article.image}
        articlePublishedTime={article.date}
        articleModifiedTime={article.date}
        articleAuthor={article.author}
        articleTags={article.tags}
        schemaData={articleSchema}
      />

      <Navbar />

      <main>
        <article>
          <div className="relative pt-24 pb-0 bg-background-50">
            <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
              <div className="mb-4">
                <Breadcrumb
                  items={[
                    { label: '首页', href: '/' },
                    { label: cat?.name || '分类', href: `/category/${article.category}` },
                    { label: article.title },
                  ]}
                />
              </div>
            </div>
          </div>

          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto pt-8 pb-6">
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <Link
                to={`/category/${article.category}`}
                className={`px-3 py-1 text-xs font-medium rounded-full whitespace-nowrap ${
                  isAccent
                    ? 'bg-accent-100 text-accent-700'
                    : 'bg-primary-100 text-primary-700'
                }`}
              >
                {cat?.name}
              </Link>
              {article.tags.map(tag => (
                <span
                  key={tag}
                  className="px-3 py-1 text-xs bg-secondary-100 text-secondary-700 rounded-full whitespace-nowrap"
                >
                  {tag}
                </span>
              ))}
            </div>

            <h1 className="text-2xl md:text-4xl font-bold font-heading text-foreground-900 leading-tight mb-6">
              {article.title}
            </h1>

            <div className="flex flex-wrap items-center gap-4 pb-8 border-b border-background-200">
              <div className="flex items-center gap-3">
                <img
                  src={article.authorAvatar}
                  alt={article.author}
                  className="w-10 h-10 rounded-full object-cover"
                />
                <div>
                  <p className="text-sm font-medium text-foreground-800 whitespace-nowrap">
                    {article.author}
                  </p>
                  <p className="text-xs text-foreground-400 whitespace-nowrap">资深作者</p>
                </div>
              </div>
              <div className="flex items-center gap-4 text-xs text-foreground-400 flex-wrap">
                <span className="whitespace-nowrap">
                  <i className="ri-calendar-line mr-1"></i>
                  {article.date}
                </span>
                <span className="whitespace-nowrap">
                  <i className="ri-time-line mr-1"></i>
                  {article.readTime}
                </span>
              </div>
            </div>
          </div>

          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto py-8">
            <div className="mb-10 rounded-2xl overflow-hidden">
              <img
                src={article.image}
                alt={article.title}
                className="w-full aspect-[16/9] object-cover"
              />
            </div>

            <div
              className="prose prose-lg max-w-none
                prose-headings:font-heading prose-headings:text-foreground-900 prose-headings:font-bold
                prose-h2:text-xl md:prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4
                prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:text-base
                prose-strong:text-foreground-900
                [&_h2]:text-xl md:[&_h2]:text-2xl [&_h2]:font-bold [&_h2]:font-heading [&_h2]:text-foreground-900 [&_h2]:mt-10 [&_h2]:mb-4
                [&_p]:text-foreground-700 [&_p]:leading-relaxed [&_p]:text-base [&_p]:mb-5
                [&_strong]:text-foreground-900"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />
          </div>

          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto py-8 border-t border-background-200">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-foreground-500 whitespace-nowrap">标签：</span>
              {article.tags.map(tag => (
                <span
                  key={tag}
                  className="px-3 py-1 text-xs bg-secondary-100 text-secondary-700 rounded-full whitespace-nowrap"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </article>

        {allRelated.length > 0 && (
          <section className="py-14 md:py-18 bg-background-100">
            <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
              <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-8">
                相关推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {allRelated.map(a => (
                  <ArticleCard key={a.id} {...a} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}